import { useCallback, useEffect, useState } from 'react'
import type { HealthResponse, MetaResponse, SessionResponse } from '../shared/types'
import { atLeast, ROLE_LABELS, type Role } from '../shared/roles'
import { api, ApiRequestError } from './api'
import { ChangePassword, EnrolTotp, Login, SecurityScreen, SetPasswordFromLink } from './auth'
import { Card, Spinner } from './components'
import { AssessmentScreen } from './screens/Assessment'
import { PatientScreen } from './screens/Patient'
import { PatientsScreen } from './screens/Patients'
import { RangesScreen } from './screens/Ranges'
import { UsersScreen } from './screens/Users'
import { SessionProvider } from './session-context'

type Phase = 'loading' | 'login' | 'ready' | 'error'
type ScreenId = 'patients' | 'ranges' | 'users' | 'security'

const SCREENS: ReadonlyArray<{ id: ScreenId; label: string; min?: Role }> = [
  { id: 'patients', label: 'Patients' },
  { id: 'ranges', label: 'Reference ranges' },
  { id: 'users', label: 'Users', min: 'admin' },
]

function linkFromHash(): { kind: 'invite' | 'reset'; token: string } | null {
  const m = /^#(invite|reset)=([A-Za-z0-9_-]+)$/.exec(window.location.hash)
  return m === null || m[1] === undefined || m[2] === undefined ? null : { kind: m[1] as 'invite' | 'reset', token: m[2] }
}

/** #assessment/12 opens an assessment directly, so links in email and Semble notes work. */
function assessmentFromHash(): number | null {
  const m = /^#assessment\/(\d+)$/.exec(window.location.hash)
  return m?.[1] === undefined ? null : Number(m[1])
}

function patientFromHash(): number | null {
  const m = /^#patient\/(\d+)$/.exec(window.location.hash)
  return m?.[1] === undefined ? null : Number(m[1])
}

export function App() {
  const [phase, setPhase] = useState<Phase>('loading')
  const [health, setHealth] = useState<HealthResponse | null>(null)
  const [meta, setMeta] = useState<MetaResponse | null>(null)
  const [session, setSession] = useState<SessionResponse | null>(null)
  const [fatal, setFatal] = useState<string | null>(null)
  const [screen, setScreen] = useState<ScreenId>('patients')
  const [assessmentId, setAssessmentId] = useState<number | null>(() => assessmentFromHash())
  const [patientId, setPatientId] = useState<number | null>(() => patientFromHash())

  useEffect(() => {
    const onHash = (): void => {
      const id = assessmentFromHash(); const pid = patientFromHash()
      if (id !== null) { setScreen('patients'); setAssessmentId(id) }
      else if (pid !== null) { setScreen('patients'); setAssessmentId(null); setPatientId(pid) }
      else { setAssessmentId(null); setPatientId(null) }
    }
    window.addEventListener('hashchange', onHash)
    return () => window.removeEventListener('hashchange', onHash)
  }, [])

  const load = useCallback(async () => {
    try {
      const s = await api.session()
      setSession(s)
      if (!s.authenticated) { setPhase('login'); return }
      const [h, m] = await Promise.all([api.health(), api.meta()])
      setHealth(h); setMeta(m); setPhase('ready')
    } catch (err) {
      if (err instanceof ApiRequestError && err.status === 401) { setPhase('login'); return }
      setFatal(err instanceof Error ? err.message : String(err)); setPhase('error')
    }
  }, [])
  useEffect(() => { void load() }, [load])

  if (phase === 'loading') return <Spinner />
  const link = linkFromHash()
  if (link !== null && phase === 'login') return <SetPasswordFromLink kind={link.kind} token={link.token} onDone={() => { window.location.hash = ''; void load() }} />
  if (phase === 'login') return <Login session={session} onChange={() => void load()} />
  if (session?.mustChangePassword === true) return <ChangePassword username={session.username ?? ''} onDone={() => void load()} />
  if (session?.mustEnrolTotp === true) return <EnrolTotp forced onDone={() => void load()} />
  if (phase === 'error') {
    return (<main><Card title="Cannot reach the API"><p>{fatal}</p><p className="note">If this is a fresh deploy, check that DATABASE_URL is set and the Postgres plugin is attached.</p></Card></main>)
  }

  const go = (id: ScreenId): void => { setAssessmentId(null); setPatientId(null); if (window.location.hash !== '') window.location.hash = ''; setScreen(id) }
  const openPatient = (id: number): void => { window.location.hash = `#patient/${id}`; setAssessmentId(null); setPatientId(id); setScreen('patients') }
  const openAssessment = (id: number): void => { window.location.hash = `#assessment/${id}` ; setAssessmentId(id); setScreen('patients') }
  const role = session?.role ?? null
  const tabs = SCREENS.filter((s) => s.min === undefined || atLeast(role, s.min))

  return (
    <SessionProvider session={session}>
      <header className="masthead">
        <div className="brand"><img className="mark" src="/brand/clinic51-logo.png" alt="Clinic51" /><h1>{meta?.appName ?? 'Results Portal'}</h1></div>
        <span className="env">
          {meta?.env} · v{health?.version}
          {meta?.semble.enabled === true ? (meta.semble.sandbox ? ' · SEMBLE SANDBOX' : ' · SEMBLE LIVE') : ' · SEMBLE OFF'}
          {health?.database.connected === false && ' · DATABASE UNREACHABLE'}
        </span>
        <span className="spacer" />
        {meta?.authEnabled === true && (
          <>
            <button type="button" disabled={session?.userId == null} title={session?.userId == null ? 'The shared password has no account to manage' : 'Your account'} onClick={() => go('security')}>
              {session?.username}{role !== null && ` · ${ROLE_LABELS[role]}`}
            </button>
            <button type="button" onClick={() => void handleLogout(load)}>Sign out</button>
          </>
        )}
      </header>
      <nav className="tabs">
        {tabs.map((s) => (
          <button key={s.id} type="button" className={s.id === screen && assessmentId === null && patientId === null ? 'active' : undefined} onClick={() => go(s.id)}>{s.label}</button>
        ))}
      </nav>
      <main>
        {assessmentId !== null ? (
          <AssessmentScreen id={assessmentId} onBack={() => go('patients')} onOpenPatient={openPatient} />
        ) : patientId !== null ? (
          <PatientScreen id={patientId} onBack={() => go('patients')} onOpenAssessment={openAssessment} />
        ) : screen === 'patients' ? (
          <PatientsScreen onOpenAssessment={openAssessment} onOpenPatient={openPatient} meta={meta} />
        ) : screen === 'ranges' ? (
          <RangesScreen />
        ) : screen === 'users' ? (
          <UsersScreen />
        ) : (
          <SecurityScreen onSessionChange={() => void load()} />
        )}
      </main>
    </SessionProvider>
  )
}

async function handleLogout(reload: () => Promise<void>) {
  await api.logout().catch(() => {})
  await reload()
}
